import { decodePolyline, mergeRoutes, toRouteSummary, type RouteSummary } from '@motogo/shared';
import { useCallback, useEffect, useRef, useState } from 'react';

import { fetchRoutes, saveRoute } from '@/lib/savedRoutes';
import { getLocalRoutes, getUnsyncedRoutes, markRouteSynced } from '@/lib/routeStore';

export type SavedRoutesState =
  | { status: 'loading' }
  /** Guest / no session — routes are account-bound (not guest-local like rides). */
  | { status: 'signedout' }
  | { status: 'ready'; routes: RouteSummary[]; offline: boolean }
  | { status: 'error'; error: string };

/**
 * Loads the saved-route library: server routes merged with the local mirror. Each load
 * first retries not-yet-uploaded saves (client GUIDs make retries idempotent), so a route
 * saved in a dead zone syncs the next time the library opens with connectivity.
 */
export function useSavedRoutes(): { state: SavedRoutesState; refresh: () => void } {
  const [state, setState] = useState<SavedRoutesState>({ status: 'loading' });
  // Ignore results from a superseded load: a refresh() that fires while an earlier load is
  // still in flight could otherwise resolve last and clobber the newer state (e.g. a just-
  // deleted route reappearing).
  const runToken = useRef(0);

  const load = useCallback(async () => {
    const token = ++runToken.current;
    const apply = (next: SavedRoutesState) => {
      if (runToken.current === token) setState(next);
    };
    apply({ status: 'loading' });

    // Sync pass before the fetch, so a just-synced route appears in the server list.
    for (const pending of await getUnsyncedRoutes()) {
      try {
        const saved = await saveRoute({
          id: pending.id,
          name: pending.name,
          description: pending.description,
          source: pending.source,
          coordinates: decodePolyline(pending.polyline).map(([lat, lon]) => [lon, lat] as [number, number]),
          durationSeconds: pending.durationSeconds,
          plan: pending.plan,
        });
        if (saved) await markRouteSynced(saved);
      } catch {
        // Still offline (or rejected) — stays queued for the next load.
      }
    }

    let server: RouteSummary[] | null = null;
    let offline = false;
    try {
      server = await fetchRoutes();
    } catch {
      offline = true; // degrade to the local mirror
    }

    if (server === null && !offline) {
      apply({ status: 'signedout' });
      return;
    }

    const local = (await getLocalRoutes()).map(toRouteSummary); // never throws
    if (offline && local.length === 0) {
      apply({ status: 'error', error: 'No connection and no locally-stored routes.' });
      return;
    }

    apply({ status: 'ready', routes: mergeRoutes(server ?? [], local), offline });
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return { state, refresh: load };
}
